import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Settings, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface ExtensionDef {
  id: string;
  titleKey: string;
  descKey: string;
  icon: string;
  configPath?: string;
}

const EXTENSIONS: ExtensionDef[] = [
  {
    id: "self-service",
    titleKey: "ext.self_service_title",
    descKey: "ext.self_service_desc",
    icon: "🔄",
    configPath: "/dashboard/settings/extensions/self-service",
  },
  {
    id: "statistics",
    titleKey: "ext.statistics_title",
    descKey: "ext.statistics_desc",
    icon: "📊",
  },
  {
    id: "pay-at-order",
    titleKey: "ext.pao_card_title",
    descKey: "ext.pao_card_desc",
    icon: "💳",
    configPath: "/dashboard/settings/extensions/pay-at-order",
  },
];

const ExtensionsSettings = () => {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [extensionStates, setExtensionStates] = useState<Record<string, { enabled: boolean; id?: string }>>({});
  const [loading, setLoading] = useState(true);

  const orgId = profile?.organisation_id;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      const { data } = await supabase
        .from("organisation_extensions")
        .select("*")
        .eq("organisation_id", orgId);
      const map: Record<string, { enabled: boolean; id?: string }> = {};
      (data || []).forEach((row: any) => {
        map[row.extension_id] = { enabled: row.enabled, id: row.id };
      });
      setExtensionStates(map);
      setLoading(false);
    };
    load();
  }, [orgId]);

  const toggleExtension = async (extId: string, enabled: boolean) => {
    if (!orgId) return;
    setExtensionStates((prev) => ({ ...prev, [extId]: { ...prev[extId], enabled } }));

    const existing = extensionStates[extId];
    if (existing?.id) {
      await supabase
        .from("organisation_extensions")
        .update({ enabled })
        .eq("id", existing.id);
    } else {
      const { data } = await supabase
        .from("organisation_extensions")
        .insert({ organisation_id: orgId, extension_id: extId, enabled })
        .select()
        .single();
      if (data) {
        setExtensionStates((prev) => ({
          ...prev,
          [extId]: { enabled, id: (data as any).id },
        }));
      }
    }
    toast({
      title: enabled ? t("ext.enabled") : t("ext.disabled"),
    });
    // Notify sidebar to update
    window.dispatchEvent(new CustomEvent("extensions-changed"));
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground text-sm">{t("ext.loading")}</div>;
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-foreground mb-1">{t("ext.title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("ext.subtitle")}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        {EXTENSIONS.map((ext) => {
          const state = extensionStates[ext.id];
          const isEnabled = state?.enabled ?? false;
          return (
            <div
              key={ext.id}
              className="rounded-xl border border-border bg-card p-5 shadow-card"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-lg">
                    {ext.icon}
                  </div>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400">
                    {t("ext.experimental")}
                  </Badge>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(val) => toggleExtension(ext.id, val)}
                />
              </div>
              <h2 className="text-sm font-semibold text-card-foreground">{t(ext.titleKey)}</h2>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t(ext.descKey)}</p>
              {ext.configPath && (
                <Button variant="outline" size="sm" asChild className="mt-3">
                  <Link to={ext.configPath}>
                    <Settings className="mr-2 h-3.5 w-3.5" />
                    {t("ext.configure")}
                  </Link>
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExtensionsSettings;
